// This can be disabled because we are rendering the fragment spread in the same file using createElement, which is not caught by the rule.
/* eslint eslint-comments/no-use: off */
import {Box, Flash} from '@primer/react'
import {MESSAGES, VALUES, TEST_IDS} from '../constants'
import {Suspense, useEffect, useRef, useCallback, useMemo, useState} from 'react'
import {graphql, type LoadMoreFn, type PreloadedQuery} from 'react-relay'
import {useFragment, usePaginationFragment, usePreloadedQuery} from 'react-relay/hooks'
import {CommentLoading} from '@github-ui/commenting'

import type {IssueTimelinePaginated$key} from './__generated__/IssueTimelinePaginated.graphql'
import type {IssueTimelineQuery} from './__generated__/IssueTimelineQuery.graphql'
import type {TimelinePaginationQuery} from './__generated__/TimelinePaginationQuery.graphql'
import {useIssueViewerSubscription} from './IssueViewerSubscription'
import {HighlightedTimeline} from './HighlightedTimeline'
import {type TimelineBaseProps, TimelineItems, type TimelineItemsProps} from './TimelineItems'
import {LABELS, TimelineLoadMore, getHighlightedEvent} from '@github-ui/timeline-items'
import type {TimelinePaginationBackQuery} from './__generated__/TimelinePaginationBackQuery.graphql'
import type {
  IssueTimelineBackPaginated$data,
  IssueTimelineBackPaginated$key,
} from './__generated__/IssueTimelineBackPaginated.graphql'
import type {
  IssueTimelineFrontPaginated$data,
  IssueTimelineFrontPaginated$key,
} from './__generated__/IssueTimelineFrontPaginated.graphql'
import {HighlightedTimelineLoading} from './HighlightedTimelineLoading'
import type {TimelineItemsViewer$key} from './__generated__/TimelineItemsViewer.graphql'

export const IssueTimelineGraphqlQuery = graphql`
  query IssueTimelineQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      id
      issue(number: $number) {
        id
        ...IssueTimelinePaginated @arguments(numberOfTimelineItems: 25)
      }
    }
    viewer {
      ...TimelineItemsViewer
    }
  }
`

type IssueTimelineProps = {
  queryRef: PreloadedQuery<IssueTimelineQuery>
  onCommentReply?: (quotedComment: string) => void
  highlightedEventText?: string
  withSecondaryFetch?: boolean
} & Omit<TimelineBaseProps, 'viewer'>

type IssueTimelineInternalProps = Omit<IssueTimelineProps, 'queryRef' | 'relayConnectionIds'> & {
  issue: IssueTimelinePaginated$key
  viewer: TimelineItemsViewer$key
}

type IssueTimelineInternalMultiple = Omit<IssueTimelineInternalProps, 'issue'> & {
  issue: IssueTimelineFrontPaginated$key & IssueTimelineBackPaginated$key
}

type IssueTimelineInternalFragmentProps = Omit<IssueTimelineProps, 'queryRef'> & {
  data: IssueTimelineFrontPaginated$data
  backData: IssueTimelineBackPaginated$data
  viewer: TimelineItemsViewer$key
  loadNext: LoadMoreFn<TimelinePaginationQuery>
  loadPrevious: LoadMoreFn<TimelinePaginationBackQuery>
}

type BackTimelineProps = {
  backData: IssueTimelineBackPaginated$data
  viewer: TimelineItemsViewer$key
} & TimelineBaseProps

export function IssueTimeline({queryRef, ...rest}: IssueTimelineProps) {
  const {repository, viewer} = usePreloadedQuery<IssueTimelineQuery>(IssueTimelineGraphqlQuery, queryRef)

  return repository && repository.issue ? (
    <IssueTimelineInternal issue={repository.issue} viewer={viewer} {...rest} />
  ) : null
}

export function IssueTimelineInternal({issue, ...rest}: IssueTimelineInternalProps) {
  const data = useFragment(
    graphql`
      fragment IssueTimelinePaginated on Issue @argumentDefinitions(numberOfTimelineItems: {type: "Int"}) {
        author {
          login
        }
        ...IssueTimelineFrontPaginated @arguments(numberOfTimelineItems: $numberOfTimelineItems)
        ...IssueTimelineBackPaginated @arguments(last: 0)
      }
    `,
    issue,
  )
  return <IssueTimelineInternalMultiple issue={data} commentSubjectAuthorLogin={data.author?.login} {...rest} />
}

function IssueTimelineInternalMultiple({issue, ...rest}: IssueTimelineInternalMultiple) {
  const {data, loadNext} = usePaginationFragment<TimelinePaginationQuery, IssueTimelineFrontPaginated$key>(
    graphql`
      fragment IssueTimelineFrontPaginated on Issue
      @argumentDefinitions(cursor: {type: "String"}, numberOfTimelineItems: {type: "Int"})
      @refetchable(queryName: "TimelinePaginationQuery") {
        frontTimeline: timelineItems(first: $numberOfTimelineItems, after: $cursor, visibleEventsOnly: true)
          @connection(key: "Issue_frontTimeline") {
          __id
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on TimelineEvent {
                databaseId
              }
              __id
              __typename
            }
          }
          ...TimelineItemsPaginated
        }
        id
        url
        isTransferInProgress
        ...LockedEventViewer
      }
    `,
    issue,
  )

  const {data: backData, loadPrevious} = usePaginationFragment<
    TimelinePaginationBackQuery,
    IssueTimelineBackPaginated$key
  >(
    graphql`
      fragment IssueTimelineBackPaginated on Issue
      @argumentDefinitions(cursor: {type: "String"}, last: {type: "Int"})
      @refetchable(queryName: "TimelinePaginationBackQuery") {
        timelineItems(last: $last, before: $cursor, visibleEventsOnly: true)
          @connection(key: "IssueBacksideTimeline_timelineItems") {
          __id
          totalCount
          pageInfo {
            hasPreviousPage
            startCursor
          }
          edges {
            node {
              ... on TimelineEvent {
                databaseId
              }
              __id
              __typename
            }
          }
          ...TimelineItemsPaginated
        }
        id
        url
      }
    `,
    issue,
  )

  const connections = [data.frontTimeline.__id, backData.timelineItems.__id]

  return (
    <IssueTimelineInternalFragment
      data={data}
      backData={backData}
      loadNext={loadNext}
      loadPrevious={loadPrevious}
      relayConnectionIds={connections}
      {...rest}
    />
  )
}

function IssueTimelineInternalFragment({
  data,
  backData,
  viewer,
  loadNext,
  loadPrevious,
  onLinkClick,
  onCommentChange,
  onCommentEditCancel,
  onCommentReply,
  commentBoxConfig: commentBoxConfig,
  highlightedEventText,
  timelineEventBaseUrl,
  withLiveUpdates,
  navigate,
  withSecondaryFetch,
  relayConnectionIds,
  commentSubjectAuthorLogin,
}: IssueTimelineInternalFragmentProps) {
  const validTimelineItems = (data.frontTimeline?.edges || []).flatMap(a => (a && a.node ? [a.node] : []))
  const validBackTimelineItems = (backData.timelineItems?.edges || []).flatMap(a => (a && a.node ? [a.node] : []))
  const isTransferInProgress = data.isTransferInProgress
  const highlightedEvent = getHighlightedEvent(highlightedEventText)
  const highlightedEventId = highlightedEvent?.id
  const highlightedEventType = highlightedEvent?.prefix

  const highlightedEventLoaded = [...validTimelineItems, ...validBackTimelineItems].some(item => {
    if (`${item.databaseId}` !== highlightedEventId) {
      return false
    }
    if (highlightedEventType === 'issuecomment') {
      return item.__typename === 'IssueComment'
    }
    return item.__typename !== 'IssueComment'
  })

  const highlightRef = useRef<null | HTMLDivElement>(null)
  const numberValidBackTimelineItems = validBackTimelineItems.length
  const numberValidTimelineItems = validTimelineItems.length

  const frontTimelineHasNext = data.frontTimeline.pageInfo.hasNextPage && data.frontTimeline.pageInfo.endCursor != null
  const backTimelineHasPrev =
    backData.timelineItems.pageInfo.hasPreviousPage && backData.timelineItems.pageInfo.startCursor != null
  const undisplayedItems = data.frontTimeline?.totalCount > numberValidTimelineItems + numberValidBackTimelineItems

  const hasMoreItems = (frontTimelineHasNext || backTimelineHasPrev) && undisplayedItems
  const [withoutCommentDivider, setWithoutCommentDivider] = useState<boolean>(hasMoreItems)
  const [initialBackFetchLoading, setInitialBackFetchLoading] = useState(false)
  const [foundHighlightedEvent, setFoundHighlightedEvent] = useState(true)

  useEffect(() => {
    if (!data) return

    const el = highlightRef.current
    el && el.scrollIntoView()
  }, [data, highlightedEventId])

  const commentArgs = useMemo(
    () => ({
      viewer,
      onCommentReply,
      onLinkClick,
      commentBoxConfig,
      refAttribute: highlightRef,
      navigate,
      relayConnectionIds,
      commentSubjectAuthorLogin,
    }),
    [commentBoxConfig, commentSubjectAuthorLogin, navigate, onCommentReply, onLinkClick, relayConnectionIds, viewer],
  )

  const loadMoreTopFn = useCallback(
    (count: number, options?: {onComplete?: () => void}) => {
      const itemsLeft = data.frontTimeline?.totalCount - numberValidTimelineItems - numberValidBackTimelineItems
      const loadCount = Math.min(count, itemsLeft || 0)
      if (loadCount === 0) {
        options?.onComplete?.()
        return
      }
      loadNext(loadCount, {
        onComplete: () => {
          options?.onComplete?.()
        },
      })
    },
    [data.frontTimeline?.totalCount, loadNext, numberValidBackTimelineItems, numberValidTimelineItems],
  )

  const loadMoreBottomFn = useCallback(
    (count: number, options?: {onComplete?: () => void}) => {
      const itemsLeft = data.frontTimeline?.totalCount - numberValidTimelineItems - numberValidBackTimelineItems
      const loadCount = Math.min(count, itemsLeft || 0)
      if (loadCount === 0) {
        options?.onComplete?.()
        return
      }
      loadPrevious(loadCount, {
        onComplete: () => {
          options?.onComplete?.()
        },
      })
    },
    [data.frontTimeline?.totalCount, loadPrevious, numberValidBackTimelineItems, numberValidTimelineItems],
  )
  const showHighlightedTimeline =
    highlightedEventText && highlightedEventId && !highlightedEventLoaded && foundHighlightedEvent
  // If the highlighted event is in the front timeline, we need to load more items as usual
  const loadMoreInHighlightedTimeline = highlightedEventId && showHighlightedTimeline

  useEffect(() => {
    if (!loadMoreInHighlightedTimeline && withSecondaryFetch && numberValidBackTimelineItems === 0) {
      // relay handles the case if the load more is called multiple time while being in flight
      setInitialBackFetchLoading(true)
      loadMoreBottomFn(VALUES.timelineBackPageSize, {
        onComplete: () => {
          setInitialBackFetchLoading(false)
        },
      })
    }
  }, [loadMoreInHighlightedTimeline, loadMoreBottomFn, numberValidBackTimelineItems, withSecondaryFetch])

  useEffect(() => {
    setWithoutCommentDivider(hasMoreItems)
  }, [hasMoreItems])

  useEffect(() => {
    if (showHighlightedTimeline && validBackTimelineItems.length > 0) {
      setWithoutCommentDivider(false)
    }
  }, [showHighlightedTimeline, validBackTimelineItems.length])

  const timelineItemsProps: TimelineItemsProps = {
    issue: data.frontTimeline,
    onCommentChange,
    onCommentEditCancel,
    highlightedEvent,
    timelineEventBaseUrl,
    issueUrl: data.url,
    issueRef: data,
    type: 'front',
    ...commentArgs,
  }

  return (
    <>
      {/* 32 (VALUES.timelineAvatarSize) + 8 (ml:3) */}
      {isTransferInProgress && (
        <Flash
          aria-live="polite"
          variant="warning"
          sx={{
            mt: 3,
          }}
        >
          {MESSAGES.issueTimelineInTransfer}
        </Flash>
      )}
      <h2 className="sr-only">{LABELS.timeline.header}</h2>
      <TimelineItems {...timelineItemsProps} />
      {hasMoreItems && !showHighlightedTimeline ? (
        <TimelineLoadMore
          loadMoreTopFn={loadMoreTopFn}
          loadMoreBottomFn={loadMoreBottomFn}
          numberOfTimelineItems={
            data.frontTimeline.totalCount - numberValidTimelineItems - numberValidBackTimelineItems
          }
          type={'front'}
        />
      ) : null}
      {showHighlightedTimeline && (
        <Box data-testid={TEST_IDS.highlightedTimeline}>
          <Suspense fallback={<HighlightedTimelineLoading />}>
            <HighlightedTimeline
              beforeElementsLoadedCount={numberValidTimelineItems}
              afterElementsLoadedCount={numberValidBackTimelineItems}
              loadFromTopFn={loadNext}
              loadMoreBottomFn={loadPrevious}
              issueId={data.id}
              issueRef={data}
              onCommentChange={onCommentChange}
              onCommentEditCancel={onCommentEditCancel}
              setWithoutCommentDivider={setWithoutCommentDivider}
              highlightedEventText={highlightedEventText}
              withSecondaryFetch={withSecondaryFetch}
              setFoundHighlightedEvent={setFoundHighlightedEvent}
              {...commentArgs}
            />
          </Suspense>
        </Box>
      )}
      {initialBackFetchLoading && <CommentLoading />}
      <BackTimeline
        {...{
          issueRef: data,
          withLiveUpdates,
          withoutCommentDivider,
          backData,
          onCommentChange,
          onCommentEditCancel,
          highlightedEvent,
          timelineEventBaseUrl,
          issueUrl: data.url,
          ...commentArgs,
        }}
      />
    </>
  )
}

function BackTimeline({
  issueRef,
  backData,
  viewer,
  onLinkClick,
  onCommentReply,
  commentBoxConfig,
  onCommentChange,
  onCommentEditCancel,
  highlightedEvent,
  timelineEventBaseUrl,
  withLiveUpdates,
  navigate,
  relayConnectionIds,
}: BackTimelineProps) {
  // this is a feature flag value so it can be changed at runtime
  if (withLiveUpdates) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useIssueViewerSubscription(backData.id, backData.timelineItems.totalCount)
  }

  const commentArgs = {
    viewer,
    onLinkClick,
    onCommentReply,
    commentBoxConfig,
    navigate,
  }

  const timelineItemsProps: TimelineItemsProps = {
    issueRef,
    issue: backData.timelineItems,
    onCommentChange,
    onCommentEditCancel,
    timelineEventBaseUrl,
    issueUrl: backData.url,
    type: 'back',
    relayConnectionIds,
    highlightedEvent,
    ...commentArgs,
  }

  return <TimelineItems {...timelineItemsProps} />
}

try{ IssueTimeline.displayName ||= 'IssueTimeline' } catch {}
try{ IssueTimelineInternal.displayName ||= 'IssueTimelineInternal' } catch {}
try{ IssueTimelineInternalMultiple.displayName ||= 'IssueTimelineInternalMultiple' } catch {}
try{ IssueTimelineInternalFragment.displayName ||= 'IssueTimelineInternalFragment' } catch {}
try{ BackTimeline.displayName ||= 'BackTimeline' } catch {}